import { BuildingOffice2Icon, UserIcon } from "@heroicons/react/24/outline";
import { ELegalStatus } from "common/enums/LegalStatus/ELegalStatus";
import { EUserFileType } from "common/enums/User/EUserFileType";
import DropdownButton, { IDropdownButtonOption } from "components/elements/DropdownButton";
import NavTabOutlet from "components/elements/NavTabOutlet";
import I18n from "components/materials/I18n";
import PageTemplate from "components/PageTemplate";
import ModuleConfig from "configs/ModuleConfig";
import { UserContext } from "contexts/User";
import useOpenable from "hooks/useOpenable";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import UserService from "services/UserService";
import { container } from "tsyringe";
import CreateSignatureModal from "../Account/CreateSigntureModal";
import classes from "./classes.module.scss";
import NewSubscriptionModal from "./NewSubscriptionModal";
import ProductSubscribedService from "services/ProductSubscribedService";
import { ESubStep } from "common/enums/Subscription/ESubStep";
import SubscriptionService from "services/SubscriptionService";

const SubscriptionPages = container.resolve(ModuleConfig).get().modules.pages.Subscriptions.props.pages;
const userService = container.resolve(UserService);
const productSubscribedService = container.resolve(ProductSubscribedService);
const subscriptionService = container.resolve(SubscriptionService);

export default function Subscriptions() {
	const { user, updateUser } = useContext(UserContext);
	const steps = useMemo(
		() => [
			ESubStep.draft,
			ESubStep.processing,
			ESubStep.waiting_signature,
			ESubStep.waiting_contrepartie,
			ESubStep.waiting_transfer_sg,
			ESubStep.waiting_validation,
			ESubStep.validated,
		],
		[],
	);

	const [draftCount, setDraftCount] = useState(0);

	const [count, setCount] = useState<{ [key in ESubStep]: number }>({
		[ESubStep.draft]: 0,
		[ESubStep.processing]: 0,
		[ESubStep.waiting_signature]: 0,
		[ESubStep.waiting_contrepartie]: 0,
		[ESubStep.waiting_transfer_sg]: 0,
		[ESubStep.waiting_validation]: 0,
		[ESubStep.validated]: 0,
	});

	const [legalStatus, setLegalStatus] = useState<ELegalStatus>(ELegalStatus.natural);
	const { isOpen, open, close } = useOpenable();
	const { isOpen: isOpenSignatureModal, open: openSignatureModal, close: closeSignatureModal } = useOpenable();

	const options: IDropdownButtonOption[] = [
		{
			title: I18n.asset.enums.ELegalStatus[ELegalStatus.natural],
			onClick: () => openNewSubscriptionModal(ELegalStatus.natural),
			icon: <UserIcon className={classes["icon"]} />,
		},
		{
			title: I18n.asset.enums.ELegalStatus[ELegalStatus.legal],
			onClick: () => openNewSubscriptionModal(ELegalStatus.legal),
			icon: <BuildingOffice2Icon className={classes["icon"]} />,
		},
	];

	const openNewSubscriptionModal = useCallback(
		(legalStatus: ELegalStatus) => {
			setLegalStatus(legalStatus);
			open();
		},
		[open],
	);

	useEffect(() => {
		subscriptionService.countDrafts().then((setCount) => {
			setDraftCount(setCount.count);
		});
	}, []);

	useEffect(() => {
		if (!user) return;

		const fetchCounts = async () => {
			const counts = await Promise.all(
				steps.map(async (step) => {
					const stepCount = await productSubscribedService.countByStep(step);

					return { step, count: stepCount.count };
				}),
			);

			setCount((prev) => {
				const updatedCounts = { ...prev };
				counts.forEach(({ step, count }) => {
					updatedCounts[step] = count;
				});
				return updatedCounts;
			});
		};

		fetchCounts();
	}, [steps, user]);

	const preventOpenIfNoSeal = useCallback(() => {
		const hasSeal = user?.userToFiles?.some((file) => file.type === EUserFileType.SEAL);
		if (!hasSeal) {
			openSignatureModal();
			return false;
		}
		return true;
	}, [user, openSignatureModal]);

	const onSignatureUpdated = useCallback(() => {
		if (!user?.email) return console.warn("User email is not defined");
		userService.getByEmail(user?.email).then((updatedUser) => updateUser(updatedUser));
		closeSignatureModal();
	}, [closeSignatureModal, updateUser, user?.email]);

	return (
		<PageTemplate
			tabTitle={I18n.asset.pages.subscriptions.page_title}
			headerTitle={I18n.asset.pages.subscriptions.page_title}
			rightElement={<DropdownButton options={options} mainTitle={I18n.asset.pages.subscriptions.dropdown_button_text} shouldOpen={preventOpenIfNoSeal} />}>
			<div className={classes["root"]}>
				<NavTabOutlet
					isCountEnabled={true}
					items={[
						{ label: I18n.asset.pages.subscriptions.sub_pages.in_progress.page_title, path: SubscriptionPages.InProgress.props.path, count: draftCount },
						{
							label: I18n.asset.pages.subscriptions.sub_pages.waiting_signature.page_title,
							path: SubscriptionPages.WaitingSignature.props.path,
							isSpecial: true,
							count: count[ESubStep.waiting_signature] + count[ESubStep.processing],
						},
						{
							label: I18n.asset.pages.subscriptions.sub_pages.waiting_contrepartie.page_title,
							path: SubscriptionPages.WaitingContrepartie.props.path,
							isSpecial: true,
							count: count[ESubStep.waiting_contrepartie],
						},
						{
							label: I18n.asset.pages.subscriptions.sub_pages.waiting_transfer_sg.page_title,
							path: SubscriptionPages.WaitingTransferSg.props.path,
							isSpecial: true,
							count: count[ESubStep.waiting_transfer_sg],
						},
						{
							label: I18n.asset.pages.subscriptions.sub_pages.waiting_validation.page_title,
							path: SubscriptionPages.WaitingValidation.props.path,
							count: count[ESubStep.waiting_validation],
						},
						{
							label: I18n.asset.pages.subscriptions.sub_pages.validated.page_title,
							path: SubscriptionPages.Validated.props.path,
							count: count[ESubStep.validated],
						},
					]}
				/>
			</div>
			<NewSubscriptionModal isOpen={isOpen} onClose={close} legalStatus={legalStatus} />
			<CreateSignatureModal
				isOpen={isOpenSignatureModal}
				onClose={closeSignatureModal}
				onSignatureUpdated={onSignatureUpdated}
				title={I18n.asset.pages.account.create_signature}
				description={I18n.asset.pages.account.signature_info}
			/>
		</PageTemplate>
	);
}
